import axios from 'axios'

// Exported so researchApi.js / portalApi.js (same backend, same auth/session)
// can reuse this instance instead of standing up a second axios client.
//
// This file must NOT import from './auth' — auth.js now owns identity via
// GET /portal/me and needs to call through `client` itself (see ensureMe()).
// An import the other way here would recreate the exact api.js<->auth.js
// cycle that router->View->api.js->router already burned us on once.
export const client = axios.create({
  // Must use the same hostname the frontend itself is served from (localhost,
  // not 127.0.0.1) - even though both are loopback/secure-context, Chrome's
  // schemeful-same-site algorithm treats "localhost" and "127.0.0.1" as
  // different sites (no shared registrable domain), so a SameSite=Strict
  // cookie set by 127.0.0.1 is silently rejected/dropped when the page is on
  // localhost (confirmed via CDP: blockedReasons: ["SchemefulSameSiteStrict"]).
  //
  // Pre-Launch-Audit (SECURITY_AUDIT_PRELAUNCH.md, B2/B3): was hardcoded to
  // http://localhost:8085 - harmless in local dev, but would have made the
  // deployed production build unusable (every visitor's browser would try
  // to reach their OWN localhost:8085). VUE_APP_API_BASE_URL is a build-time
  // env var (vue-cli-service convention) - set it to the proxied same-origin
  // path (e.g. "/api" via the Caddy reverse proxy, see deploy/Caddyfile) or
  // an absolute backend URL.
  //
  // Standard OHNE gesetzte Variable ist SAME-ORIGIN ('' = relative Pfade) - nie
  // localhost. Grund: ein Build ohne .env.production (z. B. frischer Klon)
  // lieferte am 17.10.2026 ein Bundle mit http://localhost:8085 aus und legte
  // den Prod-Login lahm. localhost gibt es nur im Dev-Server (.env.development,
  // VUE_APP_API_BASE_URL=http://localhost:8085). Der Build-Guard
  // (scripts/check-bundle.cjs, im Dockerfile) bricht bei localhost-URLs im
  // Bundle ab.
  // Note: intentionally NOT `|| ''` on a truthiness check of a different
  // default - an explicitly empty value must stay empty (same-origin).
  baseURL: typeof process.env.VUE_APP_API_BASE_URL === 'string' ? process.env.VUE_APP_API_BASE_URL : '',
  timeout: 120000,
  // Session now lives in an httpOnly cookie (meridian_session), not a
  // header the frontend reads/stores — see auth.js. withCredentials makes
  // the browser send/receive it despite frontend and backend being on
  // different ports (same-site for SameSite purposes; port isn't part of
  // "site").
  withCredentials: true,
})

function readCsrfCookie() {
  const match = document.cookie.match(/(?:^|; )meridian_csrf=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

// CSRF double-submit (Teil 5.2): mutating requests echo the JS-readable
// meridian_csrf cookie back as a header so the backend can compare the two.
// GET/HEAD/OPTIONS are exempt - the backend doesn't check them either.
client.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase()
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrf = readCsrfCookie()
    if (csrf) config.headers['X-CSRF-Token'] = csrf
  }
  return config
})

// Set by main.js, which already owns the router instance — this file must not
// import '@/router' directly, that import created a 3-file cycle
// (router/index.js -> *.vue -> api.js -> router/index.js). Called on 401
// (session missing/expired) so main.js can redirect to the right login page;
// left alone on 403 (authenticated but forbidden) - that's a page-level
// "no access" message, not a logout, per Teil 5.
let unauthorizedHandler = null
export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn
}

// Calls a visitor makes with no session at all (registration, email
// verification, resend, and login itself) - a 401 from any of these must
// never trigger the logout-and-redirect-to-login handler below.
//
// BUG (found live): a 401 from /portal/auth/login or /auth/login - the
// completely normal "wrong credentials" / "wrong portal for this account"
// response the login form already catches and displays inline - was
// ALSO caught here and treated as "your session just expired", which
// force-navigated away from whichever login page the person was actually
// filling in. Concretely: an admin using /portal-login gets a 401 (their
// account isn't external, see auth.authenticate()'s external_required
// check) -> this handler fired -> bounced them to
// /login?redirect=/portal-login -> they log in successfully there -> the
// view blindly trusts that now-stale redirect query and sends them right
// back to /portal-login, a public-only route with no auth guard, where
// they're stuck despite having a perfectly valid session. Every failed
// login attempt (not just this admin/reader mismatch) hit the same trap.
// Login endpoints must be exempt for exactly the same reason the ones
// below already are: their 401 is a form-level result, not a logout signal.
const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  '/portal/auth/login',
  '/portal/auth/register',
  '/portal/auth/verify',
  '/portal/auth/resend-verify',
  '/portal/auth/request-reset',
  '/portal/auth/reset',
  '/portal/auth/complete-signup',
]

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const url = error.config?.url || ''
    const isPublicAuthCall = PUBLIC_AUTH_PATHS.some((p) => url.includes(p))
    if (error.response?.status === 401 && unauthorizedHandler && !isPublicAuthCall) {
      unauthorizedHandler(error)
    }
    return Promise.reject(error)
  }
)

// ── Existing endpoints ─────────────────────────────────────────────────────

export function analyzeRic(ric) {
  return client.get(`/analyze/${encodeURIComponent(ric)}`)
}

export function fetchCharts(ric) {
  return client.get(`/charts/${encodeURIComponent(ric)}`)
}

export function recalculateRic(ric, payload) {
  return client.post(`/recalculate/${encodeURIComponent(ric)}`, payload)
}

export function fetchEvents(params = {}) {
  return client.get('/events', { params })
}

export function fetchAnalyzedEvents(params = {}) {
  return client.get('/events/analyzed', { params })
}

export function patchEvent(id, payload) {
  return client.patch(`/events/${id}`, payload)
}

export function fetchWatchlist(params = {}) {
  return client.get('/events/watchlist', { params })
}

// ── User Management ────────────────────────────────────────────────────────

export async function fetchUsers() {
  const resp = await client.get('/api/users')
  return { data: resp.data }
}

export async function createUser(payload) {
  const resp = await client.post('/api/users', payload)
  return resp.data
}

export async function updateUser(userId, payload) {
  const resp = await client.patch(`/api/users/${userId}`, payload)
  return resp.data
}

export async function revokeUserSessions(userId) {
  const resp = await client.delete(`/api/users/${userId}/sessions`)
  return resp.data
}

export async function unlockUser(userId) {
  const resp = await client.post(`/api/users/${userId}/unlock`)
  return resp.data
}

export async function sendResetMail(userId) {
  const resp = await client.post(`/api/users/${userId}/send-reset-mail`)
  return resp.data
}

export async function fetchLoginLog(limit = 100) {
  const resp = await client.get(`/api/login-log?limit=${limit}`)
  return resp.data
}

// ── Page permissions (Teil 6) ────────────────────────────────────────────

export async function fetchPages() {
  const resp = await client.get('/api/pages')
  return resp.data
}

export async function fetchUserPermissions(userId) {
  const resp = await client.get(`/api/users/${userId}/permissions`)
  return resp.data
}

export async function setUserPermissions(userId, pageKeys) {
  const resp = await client.put(`/api/users/${userId}/permissions`, { page_keys: pageKeys })
  return resp.data
}

// ── Search & Instruments ───────────────────────────────────────────────────

// Suche und Details: DB-Daten kommen sofort, Live-Ergaenzung hat serverseitig ein hartes Zeitlimit (~5 s); der Client wartet hoechstens 10 s.
export async function searchInstruments(query) {
  const resp = await client.get('/api/search', { params: { q: query }, timeout: 10000 })
  return resp.data
}

export async function fetchInstrumentDetails(ric) {
  const resp = await client.get(`/api/instruments/${encodeURIComponent(ric)}`, { timeout: 10000 })
  return resp.data
}

// ── Watchlist ──────────────────────────────────────────────────────────────

export async function fetchMyWatchlist() {
  const resp = await client.get('/api/watchlist')
  return resp.data
}

export async function addToWatchlist(ric, note = '') {
  const resp = await client.post('/api/watchlist', { ric, note })
  return resp.data
}

export async function removeFromWatchlist(ric) {
  const resp = await client.delete(`/api/watchlist/${encodeURIComponent(ric)}`)
  return resp.data
}

// Currency is no longer sent - the backend derives it from the RIC and
// ignores a currency field if one is present (see dcf_deviation.py).
export async function setFairValue(ric, { fair_value, note } = {}) {
  const resp = await client.patch(`/api/watchlist/${encodeURIComponent(ric)}/fair-value`, {
    fair_value, note,
  })
  return resp.data
}

// Live-Ergaenzung (Kurs, Waehrung, Name): nachgelagert und gebuendelt; das Backend antwortet innerhalb von ~3 s (hartes Limit),
// der Client wartet hoechstens 8 s - die Watchlist selbst (fetchMyWatchlist) kommt immer aus der Datenbank.
export async function fetchWatchlistLive({ refresh = false } = {}) {
  const resp = await client.get('/api/watchlist/live', { params: refresh ? { refresh: 'true' } : {}, timeout: 8000 })
  return resp.data
}

export async function fetchWatchlistPrices({ refresh = false } = {}) {
  return fetchWatchlistLive({ refresh })
}

// ── Assignments ────────────────────────────────────────────────────────────

export async function fetchMyAssignments(status = null) {
  const params = status ? { status } : {}
  const resp = await client.get('/api/assignments', { params })
  return resp.data
}

export async function respondToAssignment(assignmentId, action, rejectReason = null) {
  const resp = await client.post(`/api/assignments/${assignmentId}/respond`, {
    action,
    reject_reason: rejectReason,
  })
  return resp.data
}

export async function createAssignment(ric, assignedTo, note = '') {
  const resp = await client.post('/api/assignments', { ric, assigned_to: assignedTo, note })
  return resp.data
}

export async function fetchAllAssignments() {
  const resp = await client.get('/api/assignments/all')
  return resp.data
}

export async function fetchAnalysts() {
  const resp = await client.get('/api/users/analysts')
  return resp.data
}

// ── Report Admin (Equity Research / Market Reports) ─────────────────────────
//
// Admin-only server-side (@auth.require_auth(["admin"]), no page= check - see
// app.py comment there and ReportAdminView.vue's own menu-visibility note):
// the route guard/menu entry here are convenience only, never the real
// boundary. All of these hit /api/reports* or /api/company-logos*, matching
// this file's existing /api/* convention (portal reader endpoints live in
// portalApi.js instead).

export async function listReportsAdmin(reportType = null) {
  const resp = await client.get('/api/reports', { params: reportType ? { report_type: reportType } : {} })
  return resp.data
}

export async function createReport(payload) {
  const resp = await client.post('/api/reports', payload)
  return resp.data
}

export async function updateReport(id, payload) {
  const resp = await client.patch(`/api/reports/${id}`, payload)
  return resp.data
}

// FormData + onUploadProgress for the progress bar - NEVER set a
// Content-Type header manually here, the browser generates the multipart
// boundary itself and a hand-set header would break it (see Auftrag).
export async function uploadReportFile(id, file, onProgress) {
  const form = new FormData()
  form.append('file', file)
  const resp = await client.post(`/api/reports/${id}/file`, form, {
    onUploadProgress: onProgress ? (evt) => onProgress(evt.loaded, evt.total) : undefined,
  })
  return resp.data
}

export async function publishReport(id, published) {
  const resp = await client.post(`/api/reports/${id}/publish`, { published })
  return resp.data
}

export async function deleteReport(id) {
  const resp = await client.delete(`/api/reports/${id}`)
  return resp.data
}

export async function listCompanyLogos() {
  const resp = await client.get('/api/company-logos')
  return resp.data
}

export async function uploadCompanyLogo({ ric, company_name, file }, onProgress) {
  const form = new FormData()
  form.append('ric', ric)
  form.append('company_name', company_name)
  form.append('file', file)
  const resp = await client.post('/api/company-logos', form, {
    onUploadProgress: onProgress ? (evt) => onProgress(evt.loaded, evt.total) : undefined,
  })
  return resp.data
}

export async function deleteCompanyLogo(ric) {
  const resp = await client.delete(`/api/company-logos/${encodeURIComponent(ric)}`)
  return resp.data
}

// ── Push to Coverage (Watchlist → Portal Coverage) ───────────────────────────
//
// Admin-only server-side (@auth.require_auth(["admin"]) - see app.py comment
// on POST /api/portal-coverage/push: roles=["admin"] is the real boundary,
// not any page permission grant_all_internal_pages() might imply).

export async function prefillCoverage(ric) {
  const resp = await client.get(`/api/portal-coverage/prefill/${encodeURIComponent(ric)}`)
  return resp.data
}

export async function pushToCoverage(payload) {
  const resp = await client.post('/api/portal-coverage/push', payload)
  return resp.data
}

export async function updateCoverage(id, payload) {
  const resp = await client.patch(`/api/portal-coverage/${id}`, payload)
  return resp.data
}

export async function deleteCoverage(id) {
  const resp = await client.delete(`/api/portal-coverage/${id}`)
  return resp.data
}
